import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { MapPin, Clock, Users, Star, Heart, Share2, ArrowLeft, Loader, AlertCircle } from 'lucide-react';
import api from '../api/api';
import useAuthStore from '../store/authStore';
import BookingForm from '../components/BookingForm';
import ReviewsSection from '../components/ReviewsSection';
import LatestTours from '../components/LatestTours';

export default function TourDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [tour, setTour] = useState(null);
  const [relatedTours, setRelatedTours] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBooking, setShowBooking] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const fetchTourDetail = async () => {
      try {
        const res = await api.get(`/tours/${id}`);
        setTour(res.data.tour);
        setRelatedTours(res.data.relatedTours);
        setReviews(res.data.reviews);
      } catch (error) {
        console.error('Error fetching tour:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTourDetail();
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader className="w-8 h-8 animate-spin text-green-500" />
      </div>
    );
  }

  if (!tour) {
    return (
      <div className="container py-12 text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <p className="text-xl text-gray-600 dark:text-gray-400 mb-6">Tour not found</p>
        <Link to="/tours" className="btn-primary inline-flex items-center gap-2">
          <ArrowLeft className="w-5 h-5" />
          Back to Tours
        </Link>
      </div>
    );
  }

  const discountPercentage = tour.discount_price
    ? Math.round(((tour.price - tour.discount_price) / tour.price) * 100)
    : 0;

  return (
    <div className="bg-white dark:bg-slate-950 min-h-screen">
      <Helmet>
  <title>{tour?.title} — YS Sky Travel and Tour Agency P.L.C</title>
  <meta name="description" content={tour?.short_description || "Tour details and itinerary"} />
  <meta property="og:image" content={tour?.image} />
</Helmet>

      {/* Hero Image */}
      <div className="relative h-96 md:h-screen max-h-[600px] overflow-hidden group">
        <img
          src={`https://backend.ysskytravel.com/uploads/tours/${tour.image_url}`} 
          alt={tour.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/30" />

        {/* Discount Badge */}
        {tour.discount_price && (
          <div className="absolute top-8 right-8 bg-green-500 text-white px-6 py-3 rounded-full font-bold text-2xl shadow-lg">
            {discountPercentage}% OFF
          </div>
        )}

        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-8 left-8 bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm p-3 rounded-full hover:bg-white dark:hover:bg-slate-800 transition-colors shadow-lg"
        >
          <ArrowLeft className="w-6 h-6 text-gray-900 dark:text-white" />
        </button>
      </div>

      <div className="container py-12 md:py-16">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-8">
          <Link to="/" className="hover:text-green-500 transition-colors">Home</Link>
          <span>/</span>
          <Link to="/tours" className="hover:text-green-500 transition-colors">Tours</Link>
          <span>/</span>
          <span className="text-green-500">{tour.title}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-12">
            {/* Header */}
            <div>
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className="flex-1">
                  <div className="inline-block">
                    <span className="bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 px-3 py-1 rounded-full text-sm font-semibold">
                      {tour.category_name}
                    </span>
                  </div>
                  <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mt-4 mb-4 leading-tight">
                    {tour.title}
                  </h1>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            size={18}
                            className={i < Math.floor(tour.rating || 4) ? 'fill-green-500 text-green-500' : 'text-gray-300 dark:text-gray-600'}
                          />
                        ))}
                      </div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        ({tour.reviews_count || 0} reviews)
                      </span>
                    </div>
                  </div>
                </div>
                
              </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 dark:border-slate-800">
              <div className="flex gap-8">
                <button
                  onClick={() => setActiveTab('overview')}
                  className={`pb-4 font-semibold transition-colors ${
                    activeTab === 'overview'
                      ? 'text-green-500 border-b-2 border-green-500'
                      : 'text-gray-600 dark:text-gray-400 hover:text-green-500'
                  }`}
                >
                  Overview
                </button>
                <button
                  onClick={() => setActiveTab('reviews')}
                  className={`pb-4 font-semibold transition-colors ${
                    activeTab === 'reviews'
                      ? 'text-green-500 border-b-2 border-green-500'
                      : 'text-gray-600 dark:text-gray-400 hover:text-green-500'
                  }`}
                >
                  Reviews
                </button>
              </div>
            </div>

            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-12">
                {/* Description */}
                <section>
                  <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">About This Tour</h2>
                 <div
  className="prose max-w-none dark:prose-invert"
  dangerouslySetInnerHTML={{ __html: tour.description }}
/>

                </section>

                {/* Highlights */}
                <section>
                  <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">Tour Highlights</h2>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    <div className="bg-gradient-to-br from-green-50 to-green-100/50 dark:from-green-900/20 dark:to-green-800/10 rounded-lg p-6 border border-green-200 dark:border-green-800">
                      <Clock className="w-8 h-8 text-green-500 mb-3" />
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Duration</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">{tour.duration}</p>
                      <p className="text-xs text-gray-500">days</p>
                    </div>
                    <div className="bg-gradient-to-br from-green-50 to-green-100/50 dark:from-green-900/20 dark:to-green-800/10 rounded-lg p-6 border border-green-200 dark:border-green-800">
                      <MapPin className="w-8 h-8 text-green-500 mb-3" />
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Location</p>
                      <p className="font-bold text-gray-900 dark:text-white text-sm line-clamp-2">
                        {tour.location}
                      </p>
                    </div>
                    {tour.max_participants && (
                      <div className="bg-gradient-to-br from-green-50 to-green-100/50 dark:from-green-900/20 dark:to-green-800/10 rounded-lg p-6 border border-green-200 dark:border-green-800">
                        <Users className="w-8 h-8 text-green-500 mb-3" />
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Max Group</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">{tour.max_participants}</p>
                        <p className="text-xs text-gray-500">people</p>
                      </div>
                    )}
                    <div className="bg-gradient-to-br from-green-50 to-green-100/50 dark:from-green-900/20 dark:to-green-800/10 rounded-lg p-6 border border-green-200 dark:border-green-800">
                      <Star className="w-8 h-8 text-green-500 mb-3" />
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Rating</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        {tour.rating || 4}.0
                      </p>
                      <p className="text-xs text-gray-500">/5.0</p>
                    </div>
                  </div>
                </section>
              </div>
            )}

            {/* Reviews Tab */}
            {activeTab === 'reviews' && <ReviewsSection reviews={reviews} tourId={id} />}

            {/* Related Tours */}
            {relatedTours.length > 0 && (
              <section className="pt-12 border-t border-gray-200 dark:border-slate-800">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">Related Tours</h2>
                <LatestTours tours={relatedTours} />
              </section>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-lg p-8 sticky top-24">
              {/* Price */}
              <div className="mb-8">
                {tour.discount_price ? (
                  <>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Sale Price</p>
                    <div className="flex items-baseline gap-3">
                      <span className="text-4xl font-bold text-green-500">$ {tour.discount_price}</span>
                      <span className="text-lg text-gray-400 line-through">$ {tour.price}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Price per person</p>
                    <span className="text-4xl font-bold text-gray-900 dark:text-white">${tour.price}</span>
                  </>
                )}
              </div>

              {/* Booking Button */}
              {!showBooking ? (
                <button
                  onClick={() => {
                    if (!user) {
                      navigate('/login', { state: { from: `/tour/${id}` } });
                    } else {
                      setShowBooking(true);
                    }
                  }}
                  className="btn-primary w-full mb-4 text-lg py-3 font-semibold"
                >
                  Book Now
                </button>
              ) : (
                <BookingForm
                  tourId={tour.id}
                  price={tour.discount_price || tour.price}
                  onClose={() => setShowBooking(false)}
                />
              )}

              {/* Info Items */}
              <div className="space-y-3 pt-6 border-t border-gray-200 dark:border-slate-800">
                <div className="flex items-center gap-3 text-gray-700 dark:text-gray-300">
                  <div className="w-2 h-2 bg-green-500 rounded-full" />
                  <span className="text-sm">Instant Confirmation</span>
                </div>
                <div className="flex items-center gap-3 text-gray-700 dark:text-gray-300">
                  <div className="w-2 h-2 bg-green-500 rounded-full" />
                  <span className="text-sm">Free Cancellation</span>
                </div>
                <div className="flex items-center gap-3 text-gray-700 dark:text-gray-300">
                  <div className="w-2 h-2 bg-green-500 rounded-full" />
                  <span className="text-sm">Secure Payment</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
